/**
 * Auth Service
 *
 * Handles authentication for support staff using Firebase Auth + Google OAuth.
 * Validates that the user has the isSupportUser custom claim.
 */

import { Injectable, inject, signal, computed, DestroyRef, NgZone, Injector, runInInjectionContext } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  Auth,
  authState,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  User,
} from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { v4 as uuidv4 } from 'uuid';
import { ENTITY_SET_VERSION } from '@app/_configs/entity-set-version';
import { log } from '@app/_services/logger';

export interface SupportUser {
  id: string;
  email: string;
  name: string;
  role: 'viewer' | 'agent' | 'admin';
}

export interface ValidateSessionResponse {
  isValid: boolean;
  user?: SupportUser;
  reason?: string;
  forceLogout?: boolean;
  claimsUpdated?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private auth = inject(Auth);
  private functions = inject(Functions);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private ngZone = inject(NgZone);
  private injector = inject(Injector);

  // Auth state
  private _firebaseUser = signal<User | null>(null);
  private _supportUser = signal<SupportUser | null>(null);
  private _isLoading = signal(true);
  private _error = signal<string | null>(null);

  // Public computed signals
  firebaseUser = this._firebaseUser.asReadonly();
  supportUser = this._supportUser.asReadonly();
  isLoading = this._isLoading.asReadonly();
  error = this._error.asReadonly();

  isAuthenticated = computed(() => {
    return this._firebaseUser() !== null && this._supportUser() !== null;
  });

  isAdmin = computed(() => {
    return this._supportUser()?.role === 'admin';
  });

  isAgent = computed(() => {
    const role = this._supportUser()?.role;
    return role === 'admin' || role === 'agent';
  });

  constructor() {
    // Listen to Firebase auth state changes using AngularFire's authState observable
    // NgZone.run is required even with zoneless Angular because Firebase Auth callbacks
    // run outside Angular's execution context, which can cause signal updates to not
    // trigger change detection properly
    authState(this.auth)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        // Run inside Angular's zone and injection context to ensure proper
        // change detection and Firebase API calls work correctly
        this.ngZone.run(() => {
          runInInjectionContext(this.injector, async () => {
            this._firebaseUser.set(user);

            if (user) {
              // User is logged in, validate their support session
              await this.validateSession();
            } else {
              // User is logged out
              this._supportUser.set(null);
              this._isLoading.set(false);
            }
          });
        });
      });
  }

  /**
   * Sign in with Google OAuth
   */
  async signInWithGoogle(): Promise<boolean> {
    this._isLoading.set(true);
    this._error.set(null);

    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account',
      });

      await signInWithPopup(this.auth, provider);
      // Auth state listener will handle the rest
      return true;
    } catch (err) {
      const error = err as Error;
      log.error('AuthService', 'Sign in failed', error);
      this._error.set(error.message || 'Failed to sign in');
      this._isLoading.set(false);
      return false;
    }
  }

  /**
   * Validate the current user's support session
   */
  async validateSession(): Promise<boolean> {
    this._isLoading.set(true);
    this._error.set(null);

    try {
      // Use EXACT same request structure as support-api.service.ts
      const callable = httpsCallable<
        {
          method: string;
          sessionId: string;
          entitySetVersion: number;
          clientInfo: { platform: string };
          data: object;
        },
        { success: boolean; data: ValidateSessionResponse }
      >(this.functions, 'v2-support-support');

      const result = await callable({
        method: 'validateSession',
        sessionId: uuidv4(),
        entitySetVersion: ENTITY_SET_VERSION,
        clientInfo: { platform: 'web' },
        data: {},
      });

      const response = result.data;

      if (response.success && response.data.isValid && response.data.user) {
        // If custom claims were updated server-side, force a token refresh
        // so the client has the new isSupportUser claim for RTDB access
        if (response.data.claimsUpdated && this._firebaseUser()) {
          log.info('AuthService', 'Custom claims updated, refreshing token');
          await this._firebaseUser()!.getIdToken(true);
        }

        this._supportUser.set(response.data.user);
        this._isLoading.set(false);
        return true;
      } else {
        // Not a valid support user — show a human-readable message
        const reasonMessages: Record<string, string> = {
          'USER_NOT_FOUND': 'You are not registered as a support user. Ask an admin to invite you.',
          'ACCESS_REVOKED': 'Your support access has been revoked.',
          'ROLE_MISMATCH': 'Your role has changed. Please sign in again.',
        };
        this._error.set(reasonMessages[response.data.reason || ''] || 'Not authorized as support user');
        this._supportUser.set(null);
        this._isLoading.set(false);

        if (response.data.forceLogout) {
          await this.signOut();
        }
        return false;
      }
    } catch (err) {
      const error = err as Error;
      log.error('AuthService', 'Session validation failed', error);
      this._error.set('Failed to validate session');
      this._supportUser.set(null);
      this._isLoading.set(false);
      return false;
    }
  }

  /**
   * Sign out the current user
   */
  async signOut(): Promise<void> {
    try {
      await signOut(this.auth);
      this._supportUser.set(null);
      this._error.set(null);
      this.router.navigate(['/login']);
    } catch (err) {
      log.error('AuthService', 'Sign out failed', err);
    }
  }

  /**
   * Check if the current user has at least the specified role
   */
  hasRole(requiredRole: 'viewer' | 'agent' | 'admin'): boolean {
    const currentRole = this._supportUser()?.role;
    if (!currentRole) return false;

    const roleHierarchy: Record<string, number> = {
      viewer: 1,
      agent: 2,
      admin: 3,
    };

    return roleHierarchy[currentRole] >= roleHierarchy[requiredRole];
  }
}
